import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FeatherModule } from 'angular-feather';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-search-bar',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FeatherModule, TranslateModule],
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss']
})
export class SearchBarComponent implements OnInit, OnDestroy, OnChanges {
  /** `inline`: white field, grey icon (lavender icon only while pointer hovers the field). */
  @Input() variant: 'default' | 'inline' = 'default';

  @HostBinding('class.search-bar--inline')
  get searchBarInlineHostClass(): boolean {
    return this.variant === 'inline';
  }

  /** Placeholder text shown inside the input (already translated if needed) */
  @Input() placeholder: string = '';

  /** i18n key for placeholder; preferred so the label updates when language changes. */
  @Input() placeholderKey: string = '';

  /** Initial value to populate the search field with */
  @Input() initialValue: string = '';

  /** Debounce time in milliseconds before emitting the search event */
  @Input() debounceMs: number = 0;

  /** When true, the search icon is shown on the left (toolbar pill layout). */
  @Input() iconLeading = false;

  @HostBinding('class.search-bar--icon-leading')
  get searchBarIconLeadingHostClass(): boolean {
    return this.iconLeading;
  }

  /** When true, clicking/focusing shows a close control (width stays the same in create+search). */
  @Input() expandable = false;

  /** When true with `expandable`, start with the close control visible. */
  @Input() initiallyExpanded = false;

  /** Label for the close control shown while expanded. */
  @Input() closeLabel = 'Close';

  /** Emits the (debounced) search term whenever the user types */
  @Output() search = new EventEmitter<string>();

  /** Emits when expandable search opens or closes. */
  @Output() expandedChange = new EventEmitter<boolean>();

  @HostBinding('class.search-bar--expanded')
  get searchBarExpandedHostClass(): boolean {
    return this.expandable && this.isExpanded;
  }

  @HostBinding('class.search-bar--expandable')
  get searchBarExpandableHostClass(): boolean {
    return this.expandable;
  }

  isExpanded = false;
  searchControl = new FormControl<string>('', { nonNullable: true });
  private searchSub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue'] && !changes['initialValue'].firstChange) {
      // When parent updates initialValue (e.g., on view switch), reflect it in the input
      this.searchControl.setValue(this.initialValue || '', { emitEvent: false });
    }
  }

  ngOnInit(): void {
    // Initialize control with provided initial value
    this.searchControl.setValue(this.initialValue || '');

    if (this.expandable && this.initiallyExpanded) {
      this.isExpanded = true;
    }

    // Base stream with distinct values
    let valueChanges$ = this.searchControl.valueChanges.pipe(
      distinctUntilChanged()
    );

    // Optionally apply debounce
    if (this.debounceMs && this.debounceMs > 0) {
      valueChanges$ = valueChanges$.pipe(debounceTime(this.debounceMs));
    }

    this.searchSub = valueChanges$.subscribe(value => {
      this.search.emit(value || '');
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  onInputFocus(): void {
    if (!this.expandable || this.isExpanded) {
      return;
    }

    this.setExpanded(true);
  }

  onContainerClick(event: MouseEvent): void {
    if (!this.expandable || this.isExpanded) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.closest('.search-close-btn')) {
      return;
    }

    this.setExpanded(true);
  }

  onCloseClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.setExpanded(false);
  }

  onEscapeKey(event: Event): void {
    if (!this.expandable || !this.isExpanded) {
      return;
    }

    event.preventDefault();
    this.setExpanded(false);
  }

  private setExpanded(expanded: boolean): void {
    if (!this.expandable || this.isExpanded === expanded) {
      return;
    }

    this.isExpanded = expanded;
    this.expandedChange.emit(expanded);
  }

  /** Used by page-toolbar to open/close expandable search programmatically. */
  forceExpanded(expanded: boolean): void {
    if (!this.expandable) {
      return;
    }
    const changed = this.isExpanded !== expanded;
    this.isExpanded = expanded;
    if (changed) {
      this.expandedChange.emit(expanded);
    }
    this.cdr.detectChanges();
  }
}
